// REST-only implementation of `IChatRepository` -- SignalR (`ChatSignalRService`) is injected and
// managed directly by the store, not wrapped here, per the plan's explicit split (this repository
// is request/response only; realtime is a long-lived connection with its own lifecycle).
//
// `currentUserId` (needed by `ChatMapper.toMessage` to compute `Message.isMine`) is threaded
// through every method as a parameter rather than read from `AuthStore` here -- `data/` must not
// import `presentation/` (see `tools/check-architecture.mjs`'s "data layer imports presentation"
// rule); the store resolves it from `AuthStore.user()?.publicUserId` and passes it in, same
// "resolve in presentation, pass into data" pattern already used for `organizationId` in
// `ClientDetailStore.loadConsents`.

import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { ChatClientPolicy } from '../../domain/models/chat-client-policy.model';
import { ChatBlock } from '../../domain/models/chat-block.model';
import { ChatAbusePreview, ChatAbusePreviewRequest, ChatAbuseReceipt, ChatAbuseSubmission } from '../../domain/models/chat-abuse-report.model';
import { Conversation } from '../../domain/models/conversation.model';
import { Message } from '../../domain/models/message.model';
import { IChatRepository } from '../../domain/repositories/chat.repository';
import { IChatAbuseReportRepository } from '../../domain/repositories/chat-abuse-report.repository';
import { ChatRemoteDatasource } from '../datasources/remote/chat.remote-datasource';
import { ChatMapper } from '../mappers/chat.mapper';

@Injectable()
export class ChatRepositoryImpl implements IChatRepository, IChatAbuseReportRepository {
  private readonly datasource = inject(ChatRemoteDatasource);
  private readonly mapper = inject(ChatMapper);

  public previewAbuseReport(request: ChatAbusePreviewRequest): Observable<ChatAbusePreview> {
    return this.datasource.previewAbuseReport(request).pipe(map(dto => ({
      previewId: dto.PreviewId,
      version: dto.Version,
      expiresAtUtc: dto.ExpiresAtUtc,
      reason: dto.Reason,
      explanation: dto.Explanation,
      evidence: dto.Evidence.map(item => ({
        messageId: item.MessageId,
        senderUserId: item.SenderUserId,
        sentAtUtc: item.SentAtUtc,
        body: item.Body,
        attachment: item.Attachment ? {
          documentPublicId: item.Attachment.DocumentPublicId,
          fileName: item.Attachment.FileName,
          contentType: item.Attachment.ContentType,
          sizeBytes: item.Attachment.SizeBytes,
        } : null,
      })),
    })));
  }

  public submitAbuseReport(request: ChatAbuseSubmission): Observable<ChatAbuseReceipt> {
    return this.datasource.submitAbuseReport(request).pipe(map(dto => ({
      reportId: dto.ReportId,
      submittedAtUtc: dto.SubmittedAtUtc,
      status: dto.Status,
    })));
  }

  public getAbuseReportReceipt(reportId: string): Observable<ChatAbuseReceipt> {
    return this.datasource.getAbuseReportReceipt(reportId).pipe(map(dto => ({
      reportId: dto.ReportId,
      submittedAtUtc: dto.SubmittedAtUtc,
      status: dto.Status,
    })));
  }

  public getChatBlock(organizationId: number): Observable<ChatBlock> {
    return this.datasource.getChatBlock(organizationId).pipe(
      map(dto => ({ isBlocked: dto.IsBlocked, blockedAtUtc: dto.BlockedAtUtc })),
    );
  }

  public blockClinicChat(organizationId: number): Observable<ChatBlock> {
    return this.datasource.blockClinicChat(organizationId).pipe(
      map(dto => ({ isBlocked: dto.IsBlocked, blockedAtUtc: dto.BlockedAtUtc })),
    );
  }

  public unblockClinicChat(organizationId: number): Observable<void> {
    return this.datasource.unblockClinicChat(organizationId);
  }

  public getOrCreateClientConversation(
    organizationId: number, clientId: string, departmentId?: number,
  ): Observable<Conversation> {
    return this.datasource.getOrCreateClientConversation(organizationId, clientId, departmentId).pipe(
      map(dto => this.mapper.toConversation(dto)),
    );
  }

  public getMyConversations(): Observable<readonly Conversation[]> {
    return this.datasource.getMyConversations().pipe(
      map(dtos => dtos.map(dto => this.mapper.toConversation(dto))),
    );
  }

  public getClinicConversations(departmentId?: number): Observable<readonly Conversation[]> {
    return this.datasource.getClinicConversations(departmentId).pipe(
      map(dtos => dtos.map(dto => this.mapper.toConversation(dto))),
    );
  }

  public getMessages(
    conversationId: string, beforeMessageId: string | null, pageSize: number, currentUserId: string | null = null,
  ): Observable<{ readonly messages: readonly Message[]; readonly hasMore: boolean }> {
    // `ChatRepository.GetMessagesAsync` (backend) paginates newest-first internally (keyset/cursor,
    // `WHERE Id < beforeMessageId`) but re-sorts that page back into chronological (oldest-first)
    // order before returning it -- `dto.Items` already matches the order this feature's UI expects
    // (composer at the bottom, history scrolling upward), no reversal needed here. `dto.HasMore` is
    // propagated as-is (previously discarded along with the rest of the old `PagedDto` envelope).
    return this.datasource.getMessages(conversationId, beforeMessageId, pageSize).pipe(
      map(dto => ({ messages: this.mapper.toMessages(dto.Items, currentUserId), hasMore: dto.HasMore })),
    );
  }

  public sendMessage(conversationId: string, body: string, clientMessageId: string, currentUserId: string | null = null): Observable<Message> {
    return this.datasource.sendMessage(conversationId, body, clientMessageId).pipe(
      map(dto => this.mapper.toMessage(dto, currentUserId)),
    );
  }

  public sendFirstMessage(
    body: string, clientMessageId: string, departmentId: number, currentUserId: string | null = null,
  ): Observable<Message> {
    return this.datasource.sendFirstMessage(body, clientMessageId, departmentId).pipe(
      map(dto => this.mapper.toMessage(dto, currentUserId)),
    );
  }

  public markAsRead(conversationId: string, lastReadMessageId: string): Observable<void> {
    return this.datasource.markAsRead(conversationId, lastReadMessageId).pipe(map(() => undefined));
  }

  public markClinicalRequestsSeen(conversationId: string): Observable<void> {
    return this.datasource.markClinicalRequestsSeen(conversationId);
  }

  public hideConversation(conversationId: string): Observable<void> {
    return this.datasource.hideConversation(conversationId);
  }

  public sendAttachment(
    conversationId: string, file: File, clientMessageId: string, currentUserId: string | null = null,
    durationSeconds?: number,
  ): Observable<Message> {
    return this.datasource.sendAttachment(conversationId, file, clientMessageId, durationSeconds).pipe(
      map(dto => this.mapper.toMessage(dto, currentUserId)),
    );
  }

  public downloadAttachment(documentId: string | number): Observable<Blob> {
    return this.datasource.downloadAttachment(documentId);
  }

  public markAttachmentViewed(documentPublicId: string): Observable<void> {
    return this.datasource.markAttachmentViewed(documentPublicId);
  }

  public getMyChatPolicy(): Observable<ChatClientPolicy> {
    return this.datasource.getMyChatPolicy().pipe(map(dto => this.mapper.toChatClientPolicy(dto)));
  }
}
